import { useSyncExternalStore } from "react";

/**
 * `/mix` 総合カタログから板スレページへ遷移したことを示すURL修飾子。
 * スレページ側はこの印を見て「カタログに戻る」系の行き先だけを
 * `/mix/` へ差し替える(投稿系は板ネイティブのまま触らない)。
 */
const MIX_RETURN_QUERY_KEY = "mix";

/** 総合カタログの入口(別マウントのため素のanchorで遷移する)。 */
export const MIX_CATALOG_HREF = "/mix/";

/**
 * 板規約(4板同梱・差異トグル付き)の共通配信URL。
 * `/mix/board_rules.html` は nginx の `location /mix` が実ファイルへ解決するため、
 * 板mount(`/b/{slug}`)から素のanchorで飛んでも常に単一の実体を返せる。
 * (板mountの `/b/{slug}/board_rules.html` はthread renderer regexが吸い込む
 * ため、mount越しリンクで回避する。)
 * ハッシュ `#aimg` は board_rules.html JS の既定選択板。他板slugを指定しても
 * 板規約側で未定義slugはaimgへfallbackするので、この既定で全mountを賄う。
 */
export const BOARD_RULES_HREF = "/mix/board_rules.html#aimg";

/** `?mix=1` を付与する。既にクエリを持つhrefにも安全に足せる。 */
export function appendMixQuery(href: string): string {
  return `${href}${href.includes("?") ? "&" : "?"}${MIX_RETURN_QUERY_KEY}=1`;
}

/**
 * search値の正規化。TanStack Routerは `?mix=1` を数値1として解析するため
 * 1 / "1" / true を真として受ける(jukeboxの `?standalone` と同じ流儀)。
 */
export function isMixSearchValue(value: unknown): boolean {
  return value === 1 || value === "1" || value === true;
}

function subscribeToHistory(onChange: () => void): () => void {
  window.addEventListener("popstate", onChange);
  return (): void => window.removeEventListener("popstate", onChange);
}

/**
 * 現在URLが `/mix` 由来の印を持つか。`?pc=` と同じく生のlocationを読む
 * (routerコンテキスト外のSSR描画やテストでも安全)。router遷移でクエリが
 * 落ちた場合も、呼び出し元が遷移で再レンダーされるため追従する。
 */
export function useFromMixCatalog(): boolean {
  const search = useSyncExternalStore(
    subscribeToHistory,
    // 本番runtimeのpreact/compatはuseSyncExternalStoreの第3引数
    // (getServerSnapshot)を受け取らず黙って捨てる。window不在の保護は
    // getSnapshot側で行う(createSharedMediaQueryStoreと同じ形)。
    () => (typeof window === "undefined" ? "" : window.location.search),
    () => "",
  );
  return isMixSearchValue(
    new URLSearchParams(search).get(MIX_RETURN_QUERY_KEY) ?? undefined,
  );
}
